import { requireAction, requireOneOfStates, requireState } from "./Common";
import {
    OutOfOrderSubStateKind,
    RootStateKind,
    WelcomeSubStateKind
} from "./StateProvider";
import { ConfigClient } from "../../clients/ConfigClient";
import { OrderClient } from "../../clients/OrderClient";
import { ActionKind } from "../../types/Action";
import { ActionHandler } from "../../types/ActionHandler";
import { OperatorSubStateKind } from "../../types/Operator";
import { isValidOperatorBadge } from "../hooks/useBarcodeScanner";

export const scanOperatorBadge: ActionHandler = async (state, action) => {
    requireOneOfStates(state, RootStateKind.Welcome, RootStateKind.OutOfOrder);
    const { scannedBadge } = requireAction(
        state,
        action,
        ActionKind.ScanOperatorBadge
    );

    if (isValidOperatorBadge(scannedBadge)) {
        return {
            ...state,
            kind: RootStateKind.Operator,
            subState: { kind: OperatorSubStateKind.Idle }
        };
    }

    return {
        ...state,
        ...(state.kind === RootStateKind.Welcome
            ? {
                  kind: RootStateKind.Welcome,
                  subState: {
                      kind: WelcomeSubStateKind.Idle
                  }
              }
            : {
                  kind: RootStateKind.OutOfOrder,
                  subState: {
                      kind: OutOfOrderSubStateKind.Idle
                  }
              })
    };
};

export const showOrders: ActionHandler = async (state, action, client) => {
    const operatorState = requireState(state, RootStateKind.Operator);
    const { dateRange } = requireAction(state, action, ActionKind.ShowOrders);

    const orderClient = new OrderClient(client);
    const { orders, totalCount } = await orderClient.list(dateRange, 0);
    return {
        ...operatorState,
        kind: RootStateKind.Operator,
        subState: {
            kind: OperatorSubStateKind.Orders,
            dateRange,
            orders,
            totalCount,
            offset: 0
        }
    };
};

export const loadMoreOrders: ActionHandler = async (state, action, client) => {
    const operatorState = requireState(state, RootStateKind.Operator);
    const { offset, dateRange } = requireAction(
        state,
        action,
        ActionKind.LoadMoreOrders
    );

    const orderClient = new OrderClient(client);
    const { orders, totalCount } = await orderClient.list(dateRange, offset);
    return {
        ...operatorState,
        subState: {
            kind: OperatorSubStateKind.Orders,
            dateRange,
            offset,
            orders,
            totalCount
        }
    };
};

export const showInsights: ActionHandler = async (state, action, client) => {
    const operatorState = requireState(state, RootStateKind.Operator);
    const { dateRange, groupBy } = requireAction(
        state,
        action,
        ActionKind.ShowInsights
    );

    const orderClient = new OrderClient(client);
    const insights = await orderClient.getRevenue(dateRange, groupBy);

    return {
        ...operatorState,
        subState: {
            kind: OperatorSubStateKind.Insights,
            dateRange,
            groupBy,
            insights
        }
    };
};

export const showHardware: ActionHandler = async (state, action, _client) => {
    const operatorState = requireState(state, RootStateKind.Operator);
    requireAction(state, action, ActionKind.ShowHardware);

    return {
        ...operatorState,
        subState: {
            kind: OperatorSubStateKind.Hardware
        }
    };
};

export const showSystemInformation: ActionHandler = async (
    state,
    action,
    client
) => {
    const operatorState = requireState(state, RootStateKind.Operator);
    requireAction(state, action, ActionKind.ShowSystemInformation);

    const configClient = new ConfigClient(client);
    const systemInformation = await configClient.systemInformation();

    return {
        ...operatorState,
        subState: {
            kind: OperatorSubStateKind.SystemInformation,
            systemInformation
        }
    };
};

export const showConfiguration: ActionHandler = async (state, action) => {
    const operatorState = requireState(state, RootStateKind.Operator);
    requireAction(state, action, ActionKind.ShowConfiguration);

    return {
        ...operatorState,
        subState: {
            kind: OperatorSubStateKind.Configuration
        }
    };
};
